import each from 'lodash/each'
import Swiper from 'swiper'
import {
  Navigation,
  Pagination,
  Scrollbar,
  Grid,
  Autoplay,
  EffectCards,
  EffectCube,
  EffectFade,
  EffectFlip,
  EffectCoverflow,
  EffectCreative,
  FreeMode,
} from 'swiper/modules'
import { BP_LARGE, BP_MEDIUM, BP_XLARGE, BP_XXLARGE } from '../shared/stylesheet/Breakpoints'
import { breakpoints } from './breakpoints'

function processSettingsString(raw) {
  if (!raw) {
    return {}
  }

  try {
    return JSON.parse(raw)
  } catch (error) {
    return {}
  }
}

function normalizeSlidesPerView(value) {
  if (value === 0 || value === '0') {
    return 'auto'
  }
  return value
}

function buildResponsiveOptions(settings, baseGridFill) {
  const responsive = settings.responsive || {}
  const config = {
    [BP_MEDIUM]: breakpoints[BP_MEDIUM].size(),
    [BP_LARGE]: breakpoints[BP_LARGE].size(),
    [BP_XLARGE]: breakpoints[BP_XLARGE].size(),
    [BP_XXLARGE]: breakpoints[BP_XXLARGE].size(),
  }

  const breakpointsConfig = {}

  each(config, (size, key) => {
    const breakpointSettings = responsive[key]
    if (!breakpointSettings || breakpointSettings.inherit) {
      return
    }

    const breakpointOpts = {}
    const slidesPerView = normalizeSlidesPerView(breakpointSettings.slidesPerView)
    if (slidesPerView) {
      breakpointOpts.slidesPerView = slidesPerView
    }
    if (breakpointSettings.slidesPerGroup) {
      breakpointOpts.slidesPerGroup = breakpointSettings.slidesPerGroup
    }
    if (breakpointSettings.spaceBetween !== undefined && breakpointSettings.spaceBetween !== null) {
      breakpointOpts.spaceBetween = breakpointSettings.spaceBetween
    }

    if (breakpointSettings.gridRows) {
      breakpointOpts.grid = {
        rows: breakpointSettings.gridRows,
      }
      if (baseGridFill) {
        breakpointOpts.grid.fill = baseGridFill
      }
    }

    if (Object.keys(breakpointOpts).length) {
      breakpointsConfig[size] = breakpointOpts
    }
  })

  if (!Object.keys(breakpointsConfig).length) {
    return null
  }

  return breakpointsConfig
}

function buildSwiperOptions(el, settings) {
  const opts = {
    modules: [
      Navigation,
      Pagination,
      Scrollbar,
      Grid,
      Autoplay,
      FreeMode,
      EffectCards,
      EffectCube,
      EffectFade,
      EffectFlip,
      EffectCoverflow,
      EffectCreative,
    ],
    on: {},
  }

  const setOpt = (key, value) => {
    if (value === null || value === undefined) {
      return
    }
    opts[key] = value
  }

  const isVertical = settings.direction === 'vertical'
  const isHorizontal = !isVertical
  const slidesPerView = normalizeSlidesPerView(settings.slidesPerView)
  setOpt('autoHeight', isHorizontal ? Boolean(settings.autoHeight) : false)
  setOpt('centerInsufficientSlides', Boolean(settings.centerInsufficientSlides))
  setOpt('centeredSlides', Boolean(settings.centeredSlides))
  setOpt('centeredSlidesBounds', Boolean(settings.centeredSlidesBounds))
  setOpt('direction', settings.direction || 'horizontal')
  setOpt('effect', settings.effect || 'slide')
  setOpt('grabCursor', Boolean(settings.grabCursor))
  if ((isVertical || !settings.autoHeight) && settings.height) {
    setOpt('height', Number(settings.height))
  }
  setOpt('initialSlide', Number(settings.initialSlide || 0))
  setOpt('loop', Boolean(settings.loop))
  setOpt('loopAdditionalSlides', Number(settings.loopAdditionalSlides || 0))
  setOpt('rewind', Boolean(settings.rewind))
  setOpt('slideToClickedSlide', Boolean(settings.slideToClickedSlide))
  setOpt('slidesOffsetBefore', Number(settings.slidesOffsetBefore || 0))
  setOpt('slidesOffsetAfter', Number(settings.slidesOffsetAfter || 0))
  setOpt('slidesPerView', slidesPerView || 1)
  setOpt('slidesPerGroup', Number(settings.slidesPerGroup || 1))
  setOpt('snapToSlideEdge', Boolean(settings.slideToClickedSlide))
  setOpt('spaceBetween', Number(settings.spaceBetween || 0))
  setOpt('speed', Number(settings.speed || 300))

  if (settings.autoplay && settings.autoplay.enabled) {
    const autoplay = {}
    if (settings.autoplay.delay !== null && settings.autoplay.delay !== undefined) {
      autoplay.delay = settings.autoplay.delay * 1000
    }
    autoplay.disableOnInteraction = settings.autoplay.disableOnInteraction
    autoplay.pauseOnMouseEnter = settings.autoplay.pauseOnMouseEnter
    autoplay.stopOnLastSlide = settings.autoplay.stopOnLastSlide
    opts.autoplay = autoplay

    if (settings.autoplay.progress && settings.autoplay.progress.enabled) {
      const progressCircle = el.querySelector('.swiper-autoplay-progress svg')
      const progressContent = el.querySelector('.swiper-autoplay-progress span')
      opts.on.autoplayTimeLeft = (s, time, progress) => {
        progressCircle.style.setProperty('--progress', 1 - progress)
        progressContent.textContent = `${Math.ceil(time / 1000)}s`
      }
    }
  }

  if ((settings.effect || 'slide') === 'creative') {
    opts.creativeEffect = {
      prev: {
        translate: [0, 0, -400],
      },
      next: {
        translate: ['100%', 0, 0],
      },
    }
  }

  if (settings.freeMode && settings.freeMode.enabled) {
    opts.freeMode = {
      enabled: true,
      sticky: settings.freeMode.sticky || false,
    }
  }

  if (settings.grid) {
    const grid = {}
    if (settings.grid.rows !== null && settings.grid.rows !== undefined) {
      grid.rows = settings.grid.rows
    }
    if (settings.grid.fill !== null && settings.grid.fill !== undefined) {
      grid.fill = settings.grid.fill
    }
    if (Object.keys(grid).length) {
      opts.grid = grid
    }
  }

  if (settings.pagination && settings.pagination.enabled) {
    opts.pagination = {
      el: '.swiper-pagination',
      clickable: true,
      type: settings.pagination.type || 'bullets',
    }
  }

  if (settings.scrollbar && settings.scrollbar.enabled) {
    opts.scrollbar = true
  }

  if (settings.navigation && settings.navigation.enabled) {
    opts.navigation = {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
      addIcons: !settings.navigation.useCustomIcons,
    }
  }

  const responsive = buildResponsiveOptions(settings, opts.grid?.fill)
  if (responsive) {
    opts.breakpoints = responsive
  }

  return opts
}

function setupSwiper(el) {
  const settings = processSettingsString(el.getAttribute('data-swiper'))
  const opts = buildSwiperOptions(el, settings)

  return new Swiper(el, opts)
}

/**
 * Set up swipers in the container.
 *
 * @param {HTMLElement} container
 */
function setupSwipers(container) {
  if (!container) {
    return
  }

  // Initialize existing elements:

  const existingEls = container.querySelectorAll('[data-swiper]')
  if (existingEls.length) {
    each(existingEls, (el) => {
      if (el.closest('.reveal')) {
        return
      }
      setupSwiper(el)
    })
  }

  // Observe new elements:

  const observer = new MutationObserver((mutations) => {
    each(mutations, (mutation) => {
      each(mutation.addedNodes, (node) => {
        if (node.nodeType !== 1) {
          return
        }

        const els = node.querySelectorAll('[data-swiper]')
        if (!els.length) {
          return
        }

        each(els, (el) => {
          if (el.matches('.swiper-initialized')) {
            return
          }
          setupSwiper(el)
        })
      })
    })
  })

  observer.observe(container, {
    childList: true,
    subtree: true,
    attributes: false,
    characterData: false,
  })
}

/**
 * Destroy swipers in the container.
 *
 * @param {HTMLElement} container
 */
function destroySwipers(container) {
  if (!container) {
    return
  }

  container.querySelectorAll('.swiper').forEach((el) => {
    el.swiper?.destroy()
  })
}

export { setupSwipers, destroySwipers }
